import React, { useState, useEffect, useMemo, useCallback, useContext } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Row,
  Table,
  Spinner,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import Swal from "sweetalert2"
import { examMasterApi } from "apis/ExamMasterApi"
import { categoryApi } from "apis/CategoryApi"
import { sessionApi } from "apis/SessionApi"
import { SessionContext } from "context/sessionContext"

const MAX_SUB_ASSESSMENTS = 6

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const makeAssessmentRow = () => ({
  uid: uid(),
  backendId: null,
  name: "",
  description: "",
  minMarks: "",
  maxMarks: "",
  expanded: false,
  subAssessments: [],
  newSubName: "",
})

const makeSubAssessment = (name = "", backendId = null) => ({
  uid: uid(),
  backendId,
  name,
})

const normalizeCategory = cat => ({
  id: cat.id,
  category_name: cat.category_name || cat.name || "Untitled Category",
  evaluation_type: cat.evaluation_type || "Marks Based",
  marking: cat.marking || "Subject Wise",
  classes:
    typeof cat.st_class === "string"
      ? cat.st_class
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
          .map(name => ({ id: name, class_name: name }))
      : Array.isArray(cat.st_class)
      ? cat.st_class.map(c => ({
          id: c.uid ?? c.id ?? c.name ?? c,
          class_name: c.name || c.class_name || c.group_name || String(c),
        }))
      : [],
  subjects: (cat.subjects || []).map(s => ({
    id: s.id ?? null,
    subject_code: s.subject_code,
    subject_name: s.subject_name,
    topics: Array.isArray(s.topics)
      ? s.topics.map(t => ({
          id: t.id,
          topic_name: t.topic_name,
          sequence_no: t.sequence_no,
        }))
      : [],
  })),
})

// ─────────────────────────────────────────────────────────────────────────────
// No locking logic needed — validation is handled at the API level.
// ─────────────────────────────────────────────────────────────────────────────

const ExamUpdateForm = () => {
  document.title = "Update Exam"
  const navigate = useNavigate()
  const { id } = useParams()

  const [pageLoading, setPageLoading] = useState(true)
  const [loadError, setLoadError] = useState("")
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)

  // assessmentIdMap: uid → { backendId, subs: { subUid: backendSubId } }
  const [assessmentIdMap, setAssessmentIdMap] = useState({})

  const { Session } = useContext(SessionContext)
  const [fallbackSessionId, setFallbackSessionId] = useState(null)
  const activeSessionId = Session || sessionStorage.getItem("SessionId") || fallbackSessionId
  const [assessmentRows, setAssessmentRows] = useState([makeAssessmentRow()])
  const [rowErrors, setRowErrors] = useState({})

  const [selectedCardKeys, setSelectedCardKeys] = useState([])

  // ── Category state ──
  const [allCategories, setAllCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [categoriesLoadingMore, setCategoriesLoadingMore] = useState(false)
  const [categoriesPage, setCategoriesPage] = useState(1)
  const [categoriesTotalPages, setCategoriesTotalPages] = useState(1)
  const [prefillSubjects, setPrefillSubjects] = useState([])
  const [mappingState, setMappingState] = useState({})
  const [openClassDropdownKey, setOpenClassDropdownKey] = useState(null)
  const [openCategoryDropdownKey, setOpenCategoryDropdownKey] = useState(null)

  const getClassesForCategory = useCallback(
    categoryId => {
      const cat = allCategories.find(c => String(c.id) === String(categoryId))
      return cat ? cat.classes : []
    },
    [allCategories]
  )

  const getSubjectRowsForCategory = useCallback(
    categoryId => {
      const cat = allCategories.find(c => String(c.id) === String(categoryId))
      if (!cat) return []
      const rows = []
      cat.subjects.forEach(sub => {
        const hasTopics = Array.isArray(sub.topics) && sub.topics.length > 0
        if (hasTopics) {
          rows.push({
            rowType: "subject-header",
            subject_id: sub.id,
            subject_code: sub.subject_code,
            subject_name: sub.subject_name,
            topic_id: null,
            topic_name: null,
          })
          sub.topics.forEach(topic => {
            rows.push({
              rowType: "topic",
              subject_id: sub.id,
              subject_code: sub.subject_code,
              subject_name: sub.subject_name,
              topic_id: topic.id,
              topic_name: topic.topic_name,
              checked: false,
              min_mark: "",
              max_mark: "",
            })
          })
        } else {
          rows.push({
            rowType: "subject",
            subject_id: sub.id,
            subject_code: sub.subject_code,
            subject_name: sub.subject_name,
            topic_id: null,
            topic_name: null,
            checked: false,
            min_mark: "",
            max_mark: "",
          })
        }
      })
      return rows
    },
    [allCategories]
  )

  // ── Fetch session ──
  useEffect(() => {
    const fetchSession = async () => {
      if (Session || sessionStorage.getItem("SessionId")) return
      try {
        const res = await sessionApi.GetSession()
        if (res?.data?.session && Array.isArray(res.data.session)) {
          const active = res.data.session.find(s => s.is_active)
          if (active) setFallbackSessionId(active.id)
          else if (res.data.session.length > 0)
            setFallbackSessionId(res.data.session[0].id)
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err.message)
      }
    }
    fetchSession()
  }, [Session])

  // ── Fetch exam by ID and pre-fill ──
  useEffect(() => {
    if (!id) {
      setLoadError("No exam ID provided.")
      setPageLoading(false)
      return
    }
    if (!activeSessionId) return
    const load = async () => {
      setPageLoading(true)
      try {
        const res = await examMasterApi.getExamById(id, activeSessionId)
        const data = res?.data ?? res
        let exam = null
        if (data?.status === 200 && Array.isArray(data?.exams)) {
          exam =
            data.exams.find(e => String(e.id) === String(id)) || data.exams[0]
        }
        if (!exam) {
          setLoadError("Exam not found.")
          setPageLoading(false)
          return
        }

        // Step 1 fields
        validation.setValues({
          exam_name: exam.exam_name || "",
          description: exam.description || "",
        })

        // Step 2 rows — prefill from API
        const rows = (exam.assessments || []).map(a => ({
          uid: uid(),
          backendId: a.id,
          name: a.assessment_name || "",
          description: a.description || "",
          minMarks:
            a.min_mark != null ? String(a.min_mark) : "",
          maxMarks:
            a.max_mark != null ? String(a.max_mark) : "",
          expanded: (a.sub_assessments || []).length > 0,
          subAssessments: (a.sub_assessments || []).map(sa =>
            makeSubAssessment(sa.sub_assessment_name, sa.id)
          ),
          newSubName: "",
        }))
        setAssessmentRows(rows.length > 0 ? rows : [makeAssessmentRow()])

        // Build initial assessmentIdMap from loaded data
        const idMap = {}
        rows.forEach(row => {
          const subsMap = {}
          row.subAssessments.forEach(sub => {
            if (sub.backendId) subsMap[sub.uid] = sub.backendId
          })
          idMap[row.uid] = { backendId: row.backendId, subs: subsMap }
        })
        setAssessmentIdMap(idMap)
        setPrefillSubjects(exam.assessments || [])
      } catch (err) {
        console.error("Error fetching exam:", err)
        setLoadError("An error occurred while loading exam data.")
      } finally {
        setPageLoading(false)
      }
    }
    load()
  }, [id, activeSessionId])

  // ── Fetch categories ──
  const fetchCategories = useCallback(async (page = 1) => {
    if (!activeSessionId) return
    try {
      if (page === 1) setCategoriesLoading(true)
      else setCategoriesLoadingMore(true)
      const res = await categoryApi.getAllCategory(page, activeSessionId)
      const categories = (res?.data?.categories || []).map(normalizeCategory)
      const totalPages = res?.data?.total_pages ?? 1
      setAllCategories(prev =>
        page === 1 ? categories : [...prev, ...categories]
      )
      setCategoriesTotalPages(totalPages)
      setCategoriesPage(page)
    } catch (error) {
      console.error("Error fetching categories", error)
    } finally {
      setCategoriesLoading(false)
      setCategoriesLoadingMore(false)
    }
  }, [activeSessionId])

  useEffect(() => {
    if (activeSessionId) {
      fetchCategories(1)
    }
  }, [activeSessionId])

  // ── Derived: assessment cards ──
  const assessmentCards = useMemo(() => {
    const cards = []
    assessmentRows.forEach(row => {
      const name = row.name.trim()
      if (!name) return
      const validSubs = row.subAssessments.filter(sub => sub.name.trim())
      if (validSubs.length > 0) {
        validSubs.forEach(sub => {
          cards.push({
            key: `${row.uid}:${sub.uid}`,
            rowUid: row.uid,
            subUid: sub.uid,
            label: sub.name.trim(),
            parentLabel: name,
            isSubAssessment: true,
          })
        })
      } else {
        cards.push({
          key: row.uid,
          rowUid: row.uid,
          subUid: null,
          label: name,
          parentLabel: null,
          isSubAssessment: false,
        })
      }
    })
    return cards
  }, [assessmentRows])

useEffect(() => {
  if (!prefillSubjects.length || !assessmentCards.length) return

  const newSelectedKeys = []
  const newMappingState = {}

  prefillSubjects.forEach(assessment => {
    const matchingRow = assessmentRows.find(r => r.backendId === assessment.id)
    if (!matchingRow) return

    // ── Handle sub-assessment subjects ──
    if (assessment.sub_assessments && assessment.sub_assessments.length > 0) {
      assessment.sub_assessments.forEach(subA => {
        if (!subA.subjects || subA.subjects.length === 0) return

        const matchingSub = matchingRow.subAssessments.find(
          s => s.backendId === subA.id
        )
        if (!matchingSub) return

        const card = assessmentCards.find(
          c => c.rowUid === matchingRow.uid && c.subUid === matchingSub.uid
        )
        if (!card) return

        const key = card.key
        if (!newSelectedKeys.includes(key)) newSelectedKeys.push(key)

        const byCat = {}
        subA.subjects.forEach(subj => {
          const catId = String(subj.category_id)
          if (!byCat[catId]) byCat[catId] = []
          byCat[catId].push(subj)
        })

        Object.entries(byCat).forEach(([catId, subjects]) => {
          const rawStClass = subjects[0]?.st_class || []
          newMappingState[key] = {
            categoryId: catId,
            classIds: [],
            _rawStClass: rawStClass,
            _rawSubjects: subjects,
            subjects: [],
          }
        })
      })
    }

    // ── Handle direct assessment subjects (original logic) ──
    if (assessment.subjects && assessment.subjects.length > 0) {
      const card = assessmentCards.find(
        c => c.rowUid === matchingRow.uid && !c.isSubAssessment
      )
      if (!card) return

      const key = card.key
      if (!newSelectedKeys.includes(key)) newSelectedKeys.push(key)

      const byCat = {}
      assessment.subjects.forEach(subj => {
        const catId = String(subj.category_id)
        if (!byCat[catId]) byCat[catId] = []
        byCat[catId].push(subj)
      })

      Object.entries(byCat).forEach(([catId, subjects]) => {
        const rawStClass = subjects[0]?.st_class || []
        newMappingState[key] = {
          categoryId: catId,
          classIds: [],
          _rawStClass: rawStClass,
          _rawSubjects: subjects,
          subjects: [],
        }
      })
    }
  })

  setSelectedCardKeys(newSelectedKeys)
  setMappingState(newMappingState)
  setPrefillSubjects([])
}, [prefillSubjects, assessmentCards, assessmentRows])

  const cardsByKey = useMemo(() => {
    const map = {}
    assessmentCards.forEach(c => {
      map[c.key] = c
    })
    return map
  }, [assessmentCards])

  useEffect(() => {
    if (!allCategories.length) return

    setMappingState(prev => {
      const hasUnresolved = Object.values(prev).some(
        m => m._rawStClass || m._rawSubjects
      )
      if (!hasUnresolved) return prev

      const next = { ...prev }
      Object.keys(next).forEach(key => {
        const m = next[key]
        if (!m._rawStClass && !m._rawSubjects) return

        const cat = allCategories.find(
          c => String(c.id) === String(m.categoryId)
        )
        if (!cat) return

        let resolvedClassIds = m.classIds
        if (m._rawStClass) {
          const gradePrefixes = new Set(
            m._rawStClass.map(s => s.split(" ")[0].toLowerCase())
          )
          resolvedClassIds = cat.classes
            .filter(cls => gradePrefixes.has(cls.class_name.toLowerCase()))
            .map(cls => cls.id)
        }

        let resolvedSubjects = m.subjects
        if (m._rawSubjects) {
          const templateRows = getSubjectRowsForCategory(m.categoryId)
          resolvedSubjects = templateRows.map(row => {
            const match = m._rawSubjects.find(raw => {
              if (row.rowType === "topic") {
                return (
                  String(raw.subject_code).trim() === String(row.subject_code).trim() &&
                  raw.topic_name &&
                  String(raw.topic_name).trim().toLowerCase() === String(row.topic_name).trim().toLowerCase()
                )
              } else if (row.rowType === "subject") {
                return String(raw.subject_code).trim() === String(row.subject_code).trim()
              }
              return false
            })

            if (match) {
              return {
                ...row,
                checked: true,
                min_mark: (match.min_mark !== undefined && match.min_mark !== null) ? match.min_mark : "",
                max_mark: (match.max_mark !== undefined && match.max_mark !== null) ? match.max_mark : "",
              }
            }
            return row
          })
        }

        const { _rawStClass, _rawSubjects, ...rest } = m
        next[key] = {
          ...rest,
          classIds: resolvedClassIds,
          subjects: resolvedSubjects,
        }
      })

      return next
    })
  }, [allCategories, getSubjectRowsForCategory])

  useEffect(() => {
    const validKeys = new Set(assessmentCards.map(c => c.key))
    setSelectedCardKeys(prev => {
      const filtered = prev.filter(k => validKeys.has(k))
      return filtered.length === prev.length ? prev : filtered
    })
    setMappingState(prev => {
      const staleKeys = Object.keys(prev).filter(k => !validKeys.has(k))
      if (staleKeys.length === 0) return prev // nothing to prune, don't re-render
      const next = { ...prev }
      staleKeys.forEach(k => delete next[k])
      return next
    })
  }, [assessmentCards])

  // ── Close dropdowns on outside click ──
  useEffect(() => {
    if (!openClassDropdownKey) return
    const handler = e => {
      if (!e.target.closest(".class-multiselect")) setOpenClassDropdownKey(null)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [openClassDropdownKey])

  useEffect(() => {
    if (!openCategoryDropdownKey) return
    const handler = e => {
      if (!e.target.closest(".class-multiselect"))
        setOpenCategoryDropdownKey(null)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [openCategoryDropdownKey])



  const getMapping = key =>
    mappingState[key] || { categoryId: "", classIds: [], subjects: [] }

  const handleCardToggle = key => {
    setSelectedCardKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }

  const handleCategoryChange = (key, categoryId) => {
    setMappingState(prev => ({
      ...prev,
      [key]: {
        categoryId,
        classIds: [],
        subjects: categoryId ? getSubjectRowsForCategory(categoryId) : [],
      },
    }))
  }

  const handleClassToggle = (key, classId) => {
    setMappingState(prev => {
      const current = prev[key] || {
        categoryId: "",
        classIds: [],
        subjects: [],
      }
      const classIds = current.classIds.includes(classId)
        ? current.classIds.filter(id => id !== classId)
        : [...current.classIds, classId]
      return { ...prev, [key]: { ...current, classIds } }
    })
  }

  const handleSubjectToggle = (key, subjectIndex) => {
    setMappingState(prev => {
      const current = prev[key]
      if (!current) return prev
      return {
        ...prev,
        [key]: {
          ...current,
          subjects: current.subjects.map((s, i) => {
            if (i !== subjectIndex || s.rowType === "subject-header") return s
            return {
              ...s,
              checked: !s.checked,
              min_mark: s.checked ? "" : s.min_mark,
              max_mark: s.checked ? "" : s.max_mark,
            }
          }),
        },
      }
    })
  }

  const handleSelectAllSubjects = key => {
    setMappingState(prev => {
      const current = prev[key]
      if (!current) return prev
      const selectableRows = current.subjects.filter(
        s => s.rowType !== "subject-header"
      )
      const allChecked =
        selectableRows.length > 0 && selectableRows.every(s => s.checked)
      return {
        ...prev,
        [key]: {
          ...current,
          subjects: current.subjects.map(s => {
            if (s.rowType === "subject-header") return s
            return {
              ...s,
              checked: !allChecked,
              min_mark: allChecked ? "" : s.min_mark,
              max_mark: allChecked ? "" : s.max_mark,
            }
          }),
        },
      }
    })
  }

  const handleMarkChange = (key, subjectIndex, field, value) => {
    setMappingState(prev => {
      const current = prev[key]
      if (!current) return prev
      return {
        ...prev,
        [key]: {
          ...current,
          subjects: current.subjects.map((s, i) =>
            i === subjectIndex
              ? { ...s, [field]: value === "" ? "" : Number(value) }
              : s
          ),
        },
      }
    })
  }

  const handleCategoryPanelScroll = useCallback(
    e => {
      const el = e.currentTarget
      const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40
      if (
        nearBottom &&
        !categoriesLoadingMore &&
        categoriesPage < categoriesTotalPages
      ) {
        fetchCategories(categoriesPage + 1)
      }
    },
    [
      categoriesLoadingMore,
      categoriesPage,
      categoriesTotalPages,
      fetchCategories,
    ]
  )

  // ── Step 1 Formik ──
  const step1Schema = Yup.object({
    exam_name: Yup.string()
      .required("Please Enter Exam Name")
      .test("is-empty", "Please Enter Exam Name", value => value && value.trim() !== ""),
    description: Yup.string()
      .required("Please Enter Description")
      .test("is-empty", "Please Enter Description", value => value && value.trim() !== ""),
  })

  const validation = useFormik({
    enableReinitialize: false,
    initialValues: { exam_name: "", description: "" },
    validationSchema: step === 1 ? step1Schema : null,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async values => {
      // ── STEP 1 ──
      if (step === 1) {
        setLoading(true)
        try {
          const res = await examMasterApi.addExamStepOne({
            exam_id: Number(id),
            session_id: activeSessionId,
            exam_name: values.exam_name.trim(),
            description: values.description.trim(),
          })
          const data = res?.data
          if (data?.status && data.status !== 200) {
            Swal.fire({
              text: data.msg || "Failed to save exam.",
              icon: "error",
              title: "Error",
            })
            return
          }
          setStep(2)
        } catch (err) {
          Swal.fire({
            text: err.response?.data?.msg || "Failed to save exam.",
            icon: "error",
            title: "Error",
          })
        } finally {
          setLoading(false)
        }
        return
      }

      // ── STEP 2 ──
      if (step === 2) {
        const errors = {}
        const mainAssessmentNames = []
        assessmentRows.forEach(row => {
          const trimmed = row.name.trim()
          if (!trimmed) errors[row.uid] = "Assessment name is required"
          else mainAssessmentNames.push({ uid: row.uid, name: trimmed })
        })
        const nameCounts = {}
        mainAssessmentNames.forEach(({ name }) => {
          const k = name.toLowerCase()
          nameCounts[k] = (nameCounts[k] || 0) + 1
        })
        mainAssessmentNames.forEach(({ uid: ruid, name }) => {
          if (nameCounts[name.toLowerCase()] > 1 && !errors[ruid])
            errors[
              ruid
            ] = `"${name}" is already used — assessment names must be unique`
        })
        assessmentRows.forEach(row => {
          const subNames = {}
          row.subAssessments.forEach(sub => {
            const ts = sub.name.trim()
            if (!ts) errors[sub.uid] = "Sub-assessment name is required"
            else {
              const k = ts.toLowerCase()
              subNames[k] = (subNames[k] || 0) + 1
            }
          })
          row.subAssessments.forEach(sub => {
            const ts = sub.name.trim()
            if (ts && subNames[ts.toLowerCase()] > 1 && !errors[sub.uid])
              errors[sub.uid] = `"${ts}" is already used within this assessment`
          })
        })
        if (Object.keys(errors).length > 0) {
          setRowErrors(errors)
          return
        }
        setRowErrors({})

        const assessmentsPayload = assessmentRows.map(row => {
          const existing = assessmentIdMap[row.uid]
          const entry = {
            assessment_name: row.name.trim(),
            sub_assessments: row.subAssessments
              .filter(s => s.name.trim())
              .map(sub => {
                const subEntry = { sub_assessment_name: sub.name.trim() }
                const existingSubId = existing?.subs?.[sub.uid]
                if (existingSubId) subEntry.sub_assessment_id = existingSubId
                return subEntry
              }),
          }
          if (existing?.backendId) entry.assessment_id = existing.backendId
          if (row.description.trim()) entry.description = row.description.trim()
          if (row.minMarks !== "") entry.min_mark = Number(row.minMarks)
          if (row.maxMarks !== "") entry.max_mark = Number(row.maxMarks)
          return entry
        })

        setLoading(true)
        try {
          const res = await examMasterApi.addExamStepTwo({
            exam_id: Number(id),
            assessments: assessmentsPayload,
          })
          if (res?.data?.status && res.data.status !== 200) {
            Swal.fire({
              text: res.data.msg || "Failed to save assessments.",
              icon: "error",
              title: "Error",
            })
            return
          }
          const returnedAssessments = res?.data?.assessments || []
          const idMap = {}
          assessmentRows.forEach((row, rowIdx) => {
            const ba = returnedAssessments[rowIdx]
            if (!ba) return
            const subsMap = {}
            row.subAssessments.forEach((sub, si) => {
              const bSub = (ba.sub_assessments || [])[si]
              if (bSub?.sub_assessment_id)
                subsMap[sub.uid] = bSub.sub_assessment_id
            })
            idMap[row.uid] = { backendId: ba.assessment_id, subs: subsMap }
          })
          setAssessmentIdMap(idMap)
          setAllCategories([])
          setCategoriesPage(1)
          setCategoriesTotalPages(1)
          await fetchCategories(1)
          setStep(3)
        } catch (err) {
          Swal.fire({
            text: err.response?.data?.msg || "Failed to save assessments.",
            icon: "error",
            title: "Error",
          })
        } finally {
          setLoading(false)
        }
        return
      }

      // ── STEP 3 ──
      if (step === 3) {
        if (selectedCardKeys.length === 0) {
          Swal.fire({
            text: "Please select at least one Assessment.",
            icon: "warning",
            title: "Validation",
          })
          return
        }
        for (const key of selectedCardKeys) {
          const card = cardsByKey[key]
          const mapping = getMapping(key)
          const label = card?.label || "this assessment"
          if (!mapping.categoryId) {
            Swal.fire({
              text: `Please select a Category for "${label}"`,
              icon: "warning",
              title: "Validation",
            })
            return
          }
          if (mapping.classIds.length === 0) {
            Swal.fire({
              text: `Please select at least one Class for "${label}"`,
              icon: "warning",
              title: "Validation",
            })
            return
          }
          const checkedSubjects = mapping.subjects.filter(
            s => s.rowType !== "subject-header" && s.checked
          )
          if (checkedSubjects.length === 0) {
            Swal.fire({
              text: `Please select at least one subject for "${label}"`,
              icon: "warning",
              title: "Validation",
            })
            return
          }
          const category = allCategories.find(
            c => String(c.id) === String(mapping.categoryId)
          )
          const isGradeBased = category?.evaluation_type === "Grade Based"
          if (!isGradeBased) {
            for (const subj of checkedSubjects) {
              const displayName =
                subj.rowType === "topic"
                  ? `${subj.subject_name} → ${subj.topic_name}`
                  : subj.subject_name
              // Min/max marks validation removed
            }
          }
        }

        const mappingsPayload = selectedCardKeys.map(key => {
          const card = cardsByKey[key]
          const mapping = getMapping(key)
          const category = allCategories.find(
            c => String(c.id) === String(mapping.categoryId)
          )
          const isGradeBased = category?.evaluation_type === "Grade Based"
          const isTopicWise = category?.marking === "Subject + Topic Wise"
          const checkedSubjects = mapping.subjects.filter(
            s => s.rowType !== "subject-header" && s.checked
          )
          const rowEntry = assessmentIdMap[card.rowUid] || {}
          const categoryClasses = getClassesForCategory(mapping.categoryId)
          const entry = {
            category_id: mapping.categoryId,
            st_class: mapping.classIds.map(cid => {
              const cls = categoryClasses.find(
                c => String(c.id) === String(cid)
              )
              return {
                uid: Number(cid),
                name: cls ? cls.class_name : String(cid),
              }
            }),
            subjects: checkedSubjects.map(s => {
              const subj = {
                subject_code: s.subject_code,
                subject_name: s.subject_name,
              }
              if (s.rowType === "topic" && s.topic_name)
                subj.topic_name = s.topic_name
              if (!isGradeBased) {
                subj.min_mark = Number(s.min_mark)
                subj.max_mark = Number(s.max_mark)
              }
              return subj
            }),
          }
          if (card.isSubAssessment) {
            const backendSubId = rowEntry.subs?.[card.subUid]
            if (backendSubId) entry.sub_assessment_id = backendSubId
          } else {
            const backendId = rowEntry.backendId
            if (backendId) entry.assessment_id = backendId
          }
          return entry
        })

        setLoading(true)
        try {
          const res = await examMasterApi.addExamStepThree({
            mappings: mappingsPayload,
          })
          if (res?.data?.status && res.data.status !== 200) {
            Swal.fire({
              text: res.data.msg || "Failed to save mappings.",
              icon: "error",
              title: "Error",
            })
            return
          }
          Swal.fire({
            text: res.data?.msg || "Exam updated successfully",
            icon: "success",
            title: "Success",
          })
          navigate("/exam-master")
        } catch (err) {
          Swal.fire({
            text: err.response?.data?.msg || "Failed to save mappings.",
            icon: "error",
            title: "Error",
          })
        } finally {
          setLoading(false)
        }
      }
    },
  })

  // ── Step 2 row handlers ──
  const handleAddRow = () =>
    setAssessmentRows(prev => [...prev, makeAssessmentRow()])

  const handleRowChange = (uid, field, value) => {
    setAssessmentRows(prev =>
      prev.map(row => (row.uid === uid ? { ...row, [field]: value } : row))
    )
    if (rowErrors[uid])
      setRowErrors(prev => {
        const n = { ...prev }
        delete n[uid]
        return n
      })
  }

  const handleRemoveRow = uid => {
    if (assessmentRows.length === 1) return
    setAssessmentRows(prev => prev.filter(r => r.uid !== uid))
    setRowErrors(prev => {
      const n = { ...prev }
      delete n[uid]
      return n
    })
  }

  const handleToggleExpand = uid =>
    setAssessmentRows(prev =>
      prev.map(row =>
        row.uid === uid ? { ...row, expanded: !row.expanded } : row
      )
    )

  const handleNewSubNameChange = (rowUid, value) =>
    setAssessmentRows(prev =>
      prev.map(row =>
        row.uid === rowUid ? { ...row, newSubName: value } : row
      )
    )

  const handleAddSubAssessment = rowUid => {
    setAssessmentRows(prev =>
      prev.map(row => {
        if (row.uid !== rowUid) return row
        const name = (row.newSubName || "").trim()
        if (!name || row.subAssessments.length >= MAX_SUB_ASSESSMENTS)
          return row
        const isDuplicate = row.subAssessments.some(
          sub => sub.name.trim().toLowerCase() === name.toLowerCase()
        )
        if (isDuplicate) {
          Swal.fire({
            icon: "error",
            title: "Duplicate Sub-Assessment",
            text: "This sub-assessment already exists in this assessment.",
          })
          return row
        }
        return {
          ...row,
          newSubName: "",
          subAssessments: [...row.subAssessments, makeSubAssessment(name)],
        }
      })
    )
  }

  const handleNewSubKeyDown = (rowUid, e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddSubAssessment(rowUid)
    }
  }

  const handleRemoveSubAssessment = (rowUid, subUid) => {
    setAssessmentRows(prev =>
      prev.map(row =>
        row.uid !== rowUid
          ? row
          : {
              ...row,
              subAssessments: row.subAssessments.filter(
                sub => sub.uid !== subUid
              ),
            }
      )
    )
    setRowErrors(prev => {
      const n = { ...prev }
      delete n[subUid]
      return n
    })
  }

  const handleCancel = () => navigate("/exam-master")
  const handlePrevious = () => {
    if (step === 2) setStep(1)
    else if (step === 3) setStep(2)
  }

  // ── Stepper ──
  const Stepper = () => (
    <div className="d-flex align-items-center mb-4">
      {[
        { num: 1, label: "Update Exam" },
        { num: 2, label: "Assessment" },
        { num: 3, label: "Subject Mapping" },
      ].map((s, i, arr) => (
        <React.Fragment key={s.num}>
          <div className="d-flex align-items-center">
            <div
              className={`exam-step-num ${
                step > s.num ? "done" : step === s.num ? "active" : "pending"
              }`}
            >
              {step > s.num ? "✓" : s.num}
            </div>
            <span
              className={`exam-step-label ${
                step > s.num ? "done" : step === s.num ? "active" : "pending"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < arr.length - 1 && (
            <div className={`exam-step-line ${step > s.num ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  )

  // ── Page Loading / Error ──
  if (pageLoading) {
    return (
      <React.Fragment>
        <div className="page-content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading exam data...</p>
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  if (loadError) {
    return (
      <React.Fragment>
        <div className="page-content">
          <div className="container-fluid">
            <div className="alert alert-danger mt-4">{loadError}</div>
            <Button color="secondary" onClick={() => navigate("/exam-master")}>
              Back to Exams
            </Button>
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <style>{`
        .exam-step-num { width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;transition:all 0.2s; }
        .exam-step-num.active{background:#6366f1;color:#fff}.exam-step-num.done{background:#059669;color:#fff}.exam-step-num.pending{background:#f3f4f6;color:#9ca3af;border:2px solid #e5e7eb}
        .exam-step-label{font-size:13px;font-weight:500;margin-left:10px}.exam-step-label.active{color:#1a1d2e}.exam-step-label.done{color:#059669}.exam-step-label.pending{color:#9ca3af}
        .exam-step-line{flex:1;height:2px;background:#e5e7eb;margin:0 12px;min-width:30px;transition:background 0.3s}.exam-step-line.done{background:#059669}
        .assess-table th{font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;background:#f9fafb;padding:10px 14px;white-space:nowrap;border-bottom:1px solid #e5e7eb}
        .assess-table td{padding:10px 14px;vertical-align:top;border-bottom:1px solid #f3f4f6}
        .assess-table tbody tr:last-child td{border-bottom:none}
        .row-input{width:100%;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;outline:none;color:#1a1d2e;transition:border-color 0.15s,box-shadow 0.15s}
        .row-input:focus{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,0.1)}.row-input.is-invalid{border-color:#f46a6a}
        .row-input:disabled{background:#f3f4f6;color:#9ca3af;cursor:not-allowed}
        .row-error{color:#f46a6a;font-size:11px;margin-top:4px;display:flex;align-items:center;gap:4px}
        .row-desc-input{width:100%;padding:6px 10px;border:1px solid #e5e7eb;border-radius:6px;font-size:12px;outline:none;color:#374151;background:#fafafa;transition:border-color 0.15s;resize:vertical;min-height:34px}
        .row-desc-input:focus{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,0.1);background:#fff}
        .row-desc-input:disabled{background:#f3f4f6;color:#9ca3af;cursor:not-allowed}
        .mark-cell-input{width:100%;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;outline:none;color:#1a1d2e;text-align:center !important;transition:border-color 0.15s,box-shadow 0.15s}
        .mark-cell-input:focus{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,0.1)}
        .mark-cell-input:disabled{background:#f3f4f6;color:#9ca3af;cursor:not-allowed;border-color:#e5e7eb}
        .mark-cell-input::-webkit-outer-spin-button,.mark-cell-input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
        .mark-cell-input[type="number"]{-moz-appearance:textfield}
        .action-add-btn{display:inline-flex;align-items:center;gap:4px;background:#f0fdf4;color:#059669;border:1px solid #a7f3d0;border-radius:6px;padding:5px 12px;cursor:pointer;font-size:12px;font-weight:600;transition:all 0.15s;white-space:nowrap}
        .action-add-btn:hover{background:#dcfce7;border-color:#059669}.action-add-btn:disabled{opacity:0.4;cursor:not-allowed}
        .action-sub-btn{display:inline-flex;align-items:center;gap:4px;background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe;border-radius:6px;padding:5px 12px;cursor:pointer;font-size:12px;font-weight:600;transition:all 0.15s;white-space:nowrap}
        .action-sub-btn:hover{background:#e0e7ff;border-color:#6366f1}
        .action-del-btn{display:inline-flex;align-items:center;gap:4px;background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;border-radius:6px;padding:5px 12px;cursor:pointer;font-size:12px;font-weight:600;transition:all 0.15s;white-space:nowrap}
        .action-del-btn:hover{background:#fecaca;border-color:#dc2626}.action-del-btn:disabled{opacity:0.4;cursor:not-allowed}
        .sub-panel{background:#f5f3ff;border-left:3px solid #a5b4fc;border-radius:0 8px 8px 0;padding:12px 16px 14px;margin-top:2px}
        .sub-panel-title{font-size:11px;font-weight:700;color:#4338ca;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:10px}
        .sub-input-row{display:flex;align-items:center;gap:8px;margin-bottom:10px}
        .sub-name-input{flex:1;max-width:300px;padding:6px 10px;border:1px solid #c7d2fe;border-radius:6px;font-size:13px;outline:none;color:#1a1d2e;background:#fff;transition:border-color 0.15s,box-shadow 0.15s}
        .sub-name-input:focus{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,0.1)}
        .sub-name-input:disabled{background:#f3f4f6;color:#9ca3af;cursor:not-allowed;border-color:#e5e7eb}
        .sub-add-btn{display:inline-flex;align-items:center;gap:4px;background:#6366f1;color:#fff;border:none;border-radius:6px;padding:6px 14px;cursor:pointer;font-size:12px;font-weight:600;transition:background 0.15s;white-space:nowrap;flex-shrink:0}
        .sub-add-btn:hover:not(:disabled){background:#4338ca}.sub-add-btn:disabled{background:#c7d2fe;cursor:not-allowed}
        .sub-chips-wrap{display:flex;flex-wrap:wrap;gap:6px;align-items:center}
        .sub-chip{display:inline-flex;align-items:center;gap:5px;background:#fff;border:1.5px solid #a5b4fc;border-radius:20px;padding:4px 10px 4px 12px;font-size:12px;font-weight:600;color:#3730a3;transition:border-color 0.15s,background 0.15s;max-width:200px}
        .sub-chip:hover{border-color:#6366f1;background:#eef2ff}
        .sub-chip-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .sub-chip-remove{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:50%;background:#e0e7ff;border:none;cursor:pointer;flex-shrink:0;color:#4338ca;font-size:11px;font-weight:700;line-height:1;padding:0;transition:background 0.15s,color 0.15s}
        .sub-chip-remove:hover{background:#dc2626;color:#fff}
        .sub-limit-note{font-size:11px;color:#9ca3af;font-style:italic;margin-top:6px}
        .assessment-cards-grid{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:4px}
        .assessment-card{position:relative;cursor:pointer;border:2px solid #e5e7eb;border-radius:10px;padding:10px 16px 10px 12px;background:#fff;min-width:130px;max-width:220px;flex:1 1 130px;transition:border-color 0.18s,background 0.18s,box-shadow 0.18s;user-select:none;display:flex;align-items:center;gap:8px}
        .assessment-card:hover{border-color:#a5b4fc;background:#f5f3ff}
        .assessment-card.selected{border-color:#6366f1;background:#eef2ff;box-shadow:0 0 0 3px rgba(99,102,241,0.13)}
        .assessment-card.locked{border-color:#e5e7eb;background:#f9fafb;cursor:not-allowed;opacity:0.75}
        .assessment-card.locked.selected{border-color:#9ca3af;background:#f3f4f6;box-shadow:none}
        .assessment-card-icon{width:22px;height:22px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;border:2px solid #d1d5db;background:#fff;transition:all 0.18s}
        .assessment-card.selected .assessment-card-icon{border-color:#6366f1;background:#6366f1}
        .assessment-card.locked.selected .assessment-card-icon{border-color:#9ca3af;background:#9ca3af}
        .assessment-card-parent-label{font-size:10px;font-weight:500;color:#9ca3af;display:block}
        .assessment-card-label{font-size:13px;font-weight:600;color:#374151;line-height:1.3;transition:color 0.18s}
        .assessment-card.selected .assessment-card-label{color:#3730a3}
        .locked-badge{font-size:10px;color:#9ca3af;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:10px;padding:1px 7px;margin-left:4px;font-weight:500}
        .mapping-card-header{background:#faf5ff;border-bottom:1px solid #f3f4f6}
        .mapping-card-title{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#4c1d95}
        .mapping-card-parent{font-size:11px;font-weight:500;color:#a78bfa;margin-right:4px}
        .class-chip-empty{font-size:12px;color:#9ca3af;padding:7px 0}
        .class-multiselect{position:relative}
        .class-multiselect-wrap{position:relative}
        .class-multiselect-trigger{width:100%;display:flex;align-items:center;justify-content:space-between;gap:8px;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;background:#fff;cursor:pointer;text-align:left;font-size:13px;color:#1a1d2e;transition:border-color 0.15s,box-shadow 0.15s}
        .class-multiselect-trigger:hover{border-color:#a5b4fc}
        .class-multiselect-trigger.open{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,0.1)}
        .class-multiselect-trigger:disabled{background:#f3f4f6;color:#9ca3af;cursor:not-allowed}
        .class-multiselect-trigger-text{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .class-multiselect-trigger-text.placeholder{color:#9ca3af}
        .class-multiselect-chevron{flex-shrink:0;transition:transform 0.15s}
        .class-multiselect-trigger.open .class-multiselect-chevron{transform:rotate(180deg)}
        .class-multiselect-panel{position:absolute;top:calc(100% + 4px);left:0;right:0;z-index:20;background:#fff;border:1px solid #e5e7eb;border-radius:8px;box-shadow:0 8px 24px rgba(15,23,42,0.12);max-height:240px;overflow-y:auto;padding:4px}
        .class-multiselect-option{display:flex;align-items:center;gap:8px;padding:7px 8px;border-radius:6px;cursor:pointer;font-size:13px;color:#374151}
        .class-multiselect-option:hover{background:#f5f3ff}
        .class-multiselect-option-all{font-weight:600;color:#4338ca}
        .class-multiselect-option-label{line-height:1.2}
        .class-multiselect-divider{height:1px;background:#f3f4f6;margin:4px 2px}
        .subject-mapping-table th{font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;background:#f9fafb;padding:10px 14px;white-space:nowrap;border-bottom:1px solid #e5e7eb}
        .subject-mapping-table td{padding:9px 14px;vertical-align:middle;border-bottom:1px solid #f3f4f6}
        .subject-mapping-table tbody tr:last-child td{border-bottom:none}
        .subject-mapping-table tbody tr.row-unchecked{background:#fafafa;opacity:0.65}
        .subject-mapping-table tbody tr.subject-header-row{background:#f3f4f6}
        .subject-mapping-table tbody tr.subject-header-row td{padding:7px 14px}
        .topic-row-label{display:inline-flex;align-items:center}
        .subj-checkbox-wrap{display:flex;align-items:center;justify-content:center;cursor:pointer}
        .subj-checkbox{width:17px;height:17px;border-radius:4px;flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:all 0.15s;border:2px solid #d1d5db;background:#fff;cursor:pointer}
        .subj-checkbox.checked{border-color:#6366f1;background:#6366f1}.subj-checkbox.indeterminate{border-color:#6366f1;background:#6366f1}
        .mark-input {
          width: 100%; padding: 6px 10px;
          border: 1px solid #d1d5db; border-radius: 6px;
          font-size: 13px; outline: none; color: #1a1d2e;
          text-align: center !important;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .mark-input::placeholder { text-align: center; color: #9ca3af; opacity: 1; }
        .mark-input{width:100%;padding:6px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;outline:none;color:#1a1d2e;text-align:center !important;transition:border-color 0.15s,box-shadow 0.15s}
        .mark-input:focus{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,0.1)}
        .mark-input:disabled{background:#f3f4f6;color:#9ca3af;cursor:not-allowed;border-color:#e5e7eb}
        .mark-input::-webkit-outer-spin-button,.mark-input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
        .mark-input[type="number"]{-moz-appearance:textfield}
        .category-loading-more{text-align:center;padding:8px 4px;font-size:12px;color:#9ca3af;display:flex;align-items:center;justify-content:center;gap:6px}
        .category-all-loaded{text-align:center;padding:6px 4px;font-size:11px;color:#d1d5db}
        .subj-selected-badge{display:inline-flex;align-items:center;background:#ede9fe;color:#4c1d95;border-radius:20px;padding:2px 10px;font-size:11px;font-weight:700;margin-left:10px}
        .eval-type-badge{display:inline-flex;align-items:center;border-radius:20px;padding:2px 10px;font-size:11px;font-weight:700;margin-left:8px}
        .eval-type-badge.grade{background:#fef3c7;color:#92400e}.eval-type-badge.marks{background:#d1fae5;color:#065f46}
        .locked-overlay-badge{display:inline-flex;align-items:center;gap:4px;background:#fef3c7;color:#92400e;border:1px solid #fcd34d;border-radius:6px;padding:3px 10px;font-size:11px;font-weight:600;margin-left:auto}
      `}</style>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb title="Examination" breadcrumbItem="Update Exam" />
            <Stepper />
            <div className="card">
              <div className="card-body">
                <Form
                  onSubmit={e => {
                    e.preventDefault()
                    validation.handleSubmit()
                  }}
                >
                  {/* ── STEP 1 ── */}
                  {step === 1 && (
                    <>
                      <Row className="mb-4">
                        <Col lg={12}>
                          <label
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Exam Name *
                          </label>
                          <Input
                            id="exam_name"
                            name="exam_name"
                            type="text"
                            className="form-control"
                            placeholder="e.g. Half Yearly Examination"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.exam_name}
                            invalid={
                              !!(
                                validation.touched.exam_name &&
                                validation.errors.exam_name
                              )
                            }
                          />
                          {validation.touched.exam_name &&
                            validation.errors.exam_name && (
                              <FormFeedback type="invalid">
                                {validation.errors.exam_name}
                              </FormFeedback>
                            )}
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col lg={12}>
                          <label
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            Description *
                          </label>
                          <Input
                            id="description"
                            name="description"
                            type="textarea"
                            className="form-control"
                            placeholder="Enter exam description..."
                            rows={3}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.description}
                            invalid={
                              !!(
                                validation.touched.description &&
                                validation.errors.description
                              )
                            }
                          />
                          {validation.touched.description &&
                            validation.errors.description && (
                              <FormFeedback type="invalid">
                                {validation.errors.description}
                              </FormFeedback>
                            )}
                        </Col>
                      </Row>
                      <div
                        className="d-flex justify-content-between mt-4"
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: "16px",
                        }}
                      >
                        <Button color="secondary" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Next: Assessment →"}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ── STEP 2 ── */}
                  {step === 2 && (
                    <>
                      <div className="card mb-3">
                        <div
                          className="card-header d-flex justify-content-between align-items-center"
                          style={{ borderBottom: "1px solid #f3f4f6" }}
                        >
                          <h6
                            className="mb-0"
                            style={{ fontSize: "14px", fontWeight: 600 }}
                          >
                            Assessments
                          </h6>
                          <span style={{ fontSize: "12px", color: "#6b7280" }}>
                            {assessmentRows.length}{" "}
                            {assessmentRows.length === 1 ? "row" : "rows"}
                          </span>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <Table className="mb-0 assess-table">
                            <thead>
                              <tr>
                                <th style={{ width: "60px" }}>Sr. No.</th>
                                <th style={{ width: "24%" }}>
                                  Assessment Name *
                                </th>
                                <th>
                                  Description{" "}
                                  <span
                                    style={{
                                      marginLeft: "6px",
                                      fontSize: "10px",
                                      fontWeight: 400,
                                      color: "#9ca3af",
                                      textTransform: "none",
                                      letterSpacing: 0,
                                    }}
                                  >
                                    (optional)
                                  </span>
                                </th>
                                <th
                                  style={{
                                    width: "110px",
                                    textAlign: "center",
                                  }}
                                >
                                  Min Marks
                                </th>
                                <th
                                  style={{
                                    width: "110px",
                                    textAlign: "center",
                                  }}
                                >
                                  Max Marks
                                </th>
                                <th
                                  style={{
                                    width: "190px",
                                    textAlign: "center",
                                  }}
                                >
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {assessmentRows.map((row, index) => {
                                const isLocked = row.locked || false
                                return (
                                  <React.Fragment key={row.uid}>
                                    <tr
                                      style={
                                        isLocked
                                          ? { background: "#fafafa" }
                                          : {}
                                      }
                                    >
                                      <td style={{ verticalAlign: "middle" }}>
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "26px",
                                            height: "26px",
                                            borderRadius: "50%",
                                            background: "#f3f4f6",
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {index + 1}
                                        </span>
                                        {isLocked && (
                                          <span
                                            className="locked-badge ms-1"
                                            title="This assessment has mark entries — cannot be modified"
                                          >
                                            🔒
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <input
                                          className={`row-input ${
                                            rowErrors[row.uid]
                                              ? "is-invalid"
                                              : ""
                                          }`}
                                          type="text"
                                          placeholder="Enter assessment name..."
                                          value={row.name}
                                          disabled={isLocked}
                                          onChange={e =>
                                            handleRowChange(
                                              row.uid,
                                              "name",
                                              e.target.value
                                            )
                                          }
                                        />
                                        {rowErrors[row.uid] && (
                                          <div className="row-error">
                                            <svg
                                              width="11"
                                              height="11"
                                              viewBox="0 0 20 20"
                                              fill="none"
                                            >
                                              <circle
                                                cx="10"
                                                cy="10"
                                                r="9"
                                                stroke="#f46a6a"
                                                strokeWidth="2"
                                              />
                                              <path
                                                d="M10 6v5"
                                                stroke="#f46a6a"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                              />
                                              <circle
                                                cx="10"
                                                cy="14.5"
                                                r="1"
                                                fill="#f46a6a"
                                              />
                                            </svg>
                                            {rowErrors[row.uid]}
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <textarea
                                          className="row-desc-input"
                                          rows={1}
                                          placeholder="Enter description..."
                                          value={row.description}
                                          disabled={isLocked}
                                          onChange={e =>
                                            handleRowChange(
                                              row.uid,
                                              "description",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <input
                                          className="mark-cell-input"
                                          type="number"
                                          min={0}
                                          placeholder="0"
                                          value={row.minMarks}
                                          disabled={isLocked}
                                          onChange={e =>
                                            handleRowChange(
                                              row.uid,
                                              "minMarks",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <input
                                          className="mark-cell-input"
                                          type="number"
                                          min={0}
                                          placeholder="0"
                                          value={row.maxMarks}
                                          disabled={isLocked}
                                          onChange={e =>
                                            handleRowChange(
                                              row.uid,
                                              "maxMarks",
                                              e.target.value
                                            )
                                          }
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "middle" }}>
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                          <button
                                            type="button"
                                            className="action-sub-btn"
                                            onClick={() =>
                                              handleToggleExpand(row.uid)
                                            }
                                          >
                                            {row.expanded ? "− Sub" : "+ Sub"}
                                          </button>
                                          <button
                                            type="button"
                                            className="action-del-btn"
                                            onClick={() =>
                                              handleRemoveRow(row.uid)
                                            }
                                            disabled={
                                              assessmentRows.length === 1 ||
                                              isLocked
                                            }
                                          >
                                            🗑 Delete
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                    {row.expanded && (
                                      <tr className="assess-sub-row">
                                        <td
                                          style={{
                                            borderBottom: "1px solid #f3f4f6",
                                            paddingTop: 0,
                                            paddingBottom: 8,
                                          }}
                                        />
                                        <td
                                          colSpan={5}
                                          style={{
                                            borderBottom: "1px solid #f3f4f6",
                                            paddingTop: 0,
                                            paddingBottom: 8,
                                          }}
                                        >
                                          <div className="sub-panel">
                                            <div className="sub-panel-title">
                                              Sub-assessments
                                              {row.name.trim() && (
                                                <span
                                                  style={{
                                                    fontWeight: 400,
                                                    textTransform: "none",
                                                    color: "#6366f1",
                                                  }}
                                                >
                                                  {" "}
                                                  under "{row.name.trim()}"
                                                </span>
                                              )}
                                              {isLocked && (
                                                <span
                                                  className="ms-2"
                                                  style={{
                                                    color: "#9ca3af",
                                                    fontSize: "10px",
                                                  }}
                                                >
                                                  🔒 locked
                                                </span>
                                              )}
                                            </div>
                                            {!isLocked && (
                                              <div className="sub-input-row">
                                                <input
                                                  className="sub-name-input"
                                                  type="text"
                                                  placeholder="Sub-assessment name…"
                                                  value={row.newSubName || ""}
                                                  disabled={
                                                    row.subAssessments.length >=
                                                    MAX_SUB_ASSESSMENTS
                                                  }
                                                  onChange={e =>
                                                    handleNewSubNameChange(
                                                      row.uid,
                                                      e.target.value
                                                    )
                                                  }
                                                  onKeyDown={e =>
                                                    handleNewSubKeyDown(
                                                      row.uid,
                                                      e
                                                    )
                                                  }
                                                />
                                                <button
                                                  type="button"
                                                  className="sub-add-btn"
                                                  disabled={
                                                    !(
                                                      row.newSubName || ""
                                                    ).trim() ||
                                                    row.subAssessments.length >=
                                                      MAX_SUB_ASSESSMENTS
                                                  }
                                                  onClick={() =>
                                                    handleAddSubAssessment(
                                                      row.uid
                                                    )
                                                  }
                                                >
                                                  + Add
                                                </button>
                                              </div>
                                            )}
                                            {row.subAssessments.length > 0 && (
                                              <div className="sub-chips-wrap">
                                                {row.subAssessments.map(sub => (
                                                  <span
                                                    key={sub.uid}
                                                    className="sub-chip"
                                                  >
                                                    <span
                                                      className="sub-chip-name"
                                                      title={sub.name}
                                                    >
                                                      {sub.name}
                                                    </span>
                                                    {!isLocked && (
                                                      <button
                                                        type="button"
                                                        className="sub-chip-remove"
                                                        title={`Remove "${sub.name}"`}
                                                        onClick={() =>
                                                          handleRemoveSubAssessment(
                                                            row.uid,
                                                            sub.uid
                                                          )
                                                        }
                                                      >
                                                        ✕
                                                      </button>
                                                    )}
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                            {row.subAssessments.length >=
                                              MAX_SUB_ASSESSMENTS && (
                                              <div className="sub-limit-note">
                                                Maximum {MAX_SUB_ASSESSMENTS}{" "}
                                                sub-assessments reached.
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="action-add-btn mb-2"
                        onClick={handleAddRow}
                      >
                        + Add Assessment
                      </button>

                      <div
                        className="d-flex justify-content-between mt-4"
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: "16px",
                        }}
                      >
                        <Button color="secondary" onClick={handlePrevious}>
                          Back
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Next: Subject Mapping →"}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ── STEP 3 ── */}
                  {step === 3 && (
                    <>
                      <div className="mb-4">
                        <label
                          className="form-label"
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#374151",
                            marginBottom: "10px",
                            display: "block",
                          }}
                        >
                          Select Assessment(s) *
                        </label>
                        {assessmentCards.length === 0 ? (
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#9ca3af",
                              padding: "12px 0",
                            }}
                          >
                            No assessments added in Step 2.
                          </div>
                        ) : (
                          <div className="assessment-cards-grid">
                            {assessmentCards.map(card => {
                              const isSelected = selectedCardKeys.includes(
                                card.key
                              )
                              return (
                                <div
                                  key={card.key}
                                  className={`assessment-card${
                                    isSelected ? " selected" : ""
                                  }${card.locked ? " locked" : ""}`}
                                  onClick={() => handleCardToggle(card.key)}
                                  title={
                                    card.locked
                                      ? "This assessment already has entries and cannot be changed"
                                      : undefined
                                  }
                                >
                                  <div className="assessment-card-icon">
                                    {isSelected && (
                                      <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 10 10"
                                        fill="none"
                                      >
                                        <path
                                          d="M1.5 5L4 7.5L8.5 2.5"
                                          stroke="#fff"
                                          strokeWidth="1.8"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                  <span>
                                    {card.parentLabel && (
                                      <span className="assessment-card-parent-label">
                                        {card.parentLabel}
                                      </span>
                                    )}
                                    <span className="assessment-card-label">
                                      {card.label}
                                    </span>
                                  </span>
                                  {card.locked && (
                                    <span className="locked-badge ms-auto">
                                      🔒 locked
                                    </span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        {selectedCardKeys.length > 0 && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6366f1",
                              marginTop: "6px",
                              fontWeight: 500,
                            }}
                          >
                            {selectedCardKeys.length} assessment
                            {selectedCardKeys.length > 1 ? "s" : ""} selected
                            {selectedCardKeys.filter(k => cardsByKey[k]?.locked)
                              .length > 0 && (
                              <span className="text-muted ms-2">
                                (
                                {
                                  selectedCardKeys.filter(
                                    k => cardsByKey[k]?.locked
                                  ).length
                                }{" "}
                                locked — view only)
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {selectedCardKeys.length === 0 ? (
                        <div
                          className="text-center py-5"
                          style={{ color: "#9ca3af", fontSize: "13px" }}
                        >
                          Please select at least one Assessment to map its
                          Category, Class and Subjects.
                        </div>
                      ) : (
                        selectedCardKeys.map(key => {
                          const card = cardsByKey[key]
                          if (!card) return null
                          const mapping = getMapping(key)
                          const category = allCategories.find(
                            c => String(c.id) === String(mapping.categoryId)
                          )
                          const classes = mapping.categoryId
                            ? getClassesForCategory(mapping.categoryId)
                            : []
                          const isGradeBased =
                            category?.evaluation_type === "Grade Based"
                          const isTopicWise =
                            category?.marking === "Subject + Topic Wise"
                          const selectableSubjects = mapping.subjects.filter(
                            s => s.rowType !== "subject-header"
                          )
                          const checkedCount = selectableSubjects.filter(
                            s => s.checked
                          ).length
                          const allChecked =
                            selectableSubjects.length > 0 &&
                            selectableSubjects.every(s => s.checked)
                          const someChecked =
                            selectableSubjects.some(s => s.checked) &&
                            !allChecked
                          const isLocked = card.locked

                          return (
                            <div className="card mb-3" key={key}>
                              <div className="card-header d-flex align-items-center mapping-card-header flex-wrap gap-2">
                                {card.parentLabel && (
                                  <span className="mapping-card-parent">
                                    {card.parentLabel} /
                                  </span>
                                )}
                                <h6 className="mb-0 mapping-card-title">
                                  {card.label}
                                </h6>
                                {category && (
                                  <span
                                    className={`eval-type-badge ${
                                      isGradeBased ? "grade" : "marks"
                                    }`}
                                  >
                                    {category.evaluation_type}
                                  </span>
                                )}
                                {isLocked && (
                                  <span className="locked-overlay-badge">
                                    🔒 Locked — entries exist, read-only
                                  </span>
                                )}
                                {!isLocked && selectableSubjects.length > 0 && (
                                  <span className="subj-selected-badge ms-auto">
                                    {checkedCount} / {selectableSubjects.length}{" "}
                                    selected
                                  </span>
                                )}
                              </div>

                              <div className="card-body">
                                {isLocked ? (
                                  <div
                                    className="alert alert-secondary p-2 mb-0"
                                    style={{
                                      fontSize: "12px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    <i className="bx bx-lock me-1" />
                                    This assessment already has mark/grade
                                    entries. No changes can be made to its
                                    subject mapping, classes, or category.
                                  </div>
                                ) : (
                                  <>
                                    <div
                                      className="category-class-row mb-4"
                                      style={{
                                        display: "flex",
                                        gap: "16px",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {/* Category dropdown */}
                                      <div
                                        style={{
                                          flex: "1 1 240px",
                                          minWidth: "200px",
                                        }}
                                      >
                                        <label
                                          className="form-label"
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: "#374151",
                                          }}
                                        >
                                          Category *
                                        </label>
                                        {categoriesLoading ? (
                                          <div
                                            style={{
                                              fontSize: "13px",
                                              color: "#9ca3af",
                                              padding: "8px 0",
                                            }}
                                          >
                                            Loading categories...
                                          </div>
                                        ) : (
                                          <div className="class-multiselect">
                                            <button
                                              type="button"
                                              className={`class-multiselect-trigger${
                                                openCategoryDropdownKey === key
                                                  ? " open"
                                                  : ""
                                              }`}
                                              onClick={() =>
                                                setOpenCategoryDropdownKey(
                                                  prev =>
                                                    prev === key ? null : key
                                                )
                                              }
                                            >
                                              <span
                                                className={`class-multiselect-trigger-text${
                                                  !mapping.categoryId
                                                    ? " placeholder"
                                                    : ""
                                                }`}
                                              >
                                                {mapping.categoryId
                                                  ? allCategories.find(
                                                      c =>
                                                        String(c.id) ===
                                                        String(
                                                          mapping.categoryId
                                                        )
                                                    )?.category_name ??
                                                    "Select Category"
                                                  : "Select Category"}
                                              </span>
                                              <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                className="class-multiselect-chevron"
                                              >
                                                <path
                                                  d="M2.5 4.5L6 8L9.5 4.5"
                                                  stroke="#6b7280"
                                                  strokeWidth="1.6"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                            </button>
                                            {openCategoryDropdownKey ===
                                              key && (
                                              <div
                                                className="class-multiselect-panel"
                                                onScroll={
                                                  handleCategoryPanelScroll
                                                }
                                              >
                                                <div
                                                  className="class-multiselect-option"
                                                  onClick={() => {
                                                    handleCategoryChange(
                                                      key,
                                                      ""
                                                    )
                                                    setOpenCategoryDropdownKey(
                                                      null
                                                    )
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#9ca3af",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    Select Category
                                                  </span>
                                                </div>
                                                <div className="class-multiselect-divider" />
                                                {allCategories.map(c => {
                                                  const isSel =
                                                    String(
                                                      mapping.categoryId
                                                    ) === String(c.id)
                                                  return (
                                                    <div
                                                      key={c.id}
                                                      className="class-multiselect-option"
                                                      style={{
                                                        background: isSel
                                                          ? "#eef2ff"
                                                          : undefined,
                                                        fontWeight: isSel
                                                          ? 600
                                                          : 400,
                                                        color: isSel
                                                          ? "#3730a3"
                                                          : "#374151",
                                                      }}
                                                      onClick={() => {
                                                        handleCategoryChange(
                                                          key,
                                                          c.id
                                                        )
                                                        setOpenCategoryDropdownKey(
                                                          null
                                                        )
                                                      }}
                                                    >
                                                      <span className="class-multiselect-option-label">
                                                        {c.category_name}
                                                      </span>
                                                    </div>
                                                  )
                                                })}
                                                {categoriesLoadingMore && (
                                                  <div className="category-loading-more">
                                                    <svg
                                                      width="14"
                                                      height="14"
                                                      viewBox="0 0 24 24"
                                                      fill="none"
                                                      style={{
                                                        animation:
                                                          "spin 1s linear infinite",
                                                      }}
                                                    >
                                                      <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="#d1d5db"
                                                        strokeWidth="3"
                                                      />
                                                      <path
                                                        d="M12 2a10 10 0 0 1 10 10"
                                                        stroke="#6366f1"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                      />
                                                    </svg>
                                                    Loading more...
                                                  </div>
                                                )}
                                                {!categoriesLoadingMore &&
                                                  categoriesPage >=
                                                    categoriesTotalPages &&
                                                  allCategories.length > 0 && (
                                                    <div className="category-all-loaded">
                                                      ✓ All categories loaded
                                                    </div>
                                                  )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Class multi-select */}
                                      <div
                                        className="class-multiselect"
                                        style={{
                                          flex: "1 1 240px",
                                          minWidth: "200px",
                                        }}
                                      >
                                        <label
                                          className="form-label"
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: "#374151",
                                          }}
                                        >
                                          Class *
                                        </label>
                                        {!mapping.categoryId ? (
                                          <div className="class-chip-empty">
                                            Select a category first
                                          </div>
                                        ) : classes.length === 0 ? (
                                          <div className="class-chip-empty">
                                            No classes found for this category
                                          </div>
                                        ) : (
                                          <div className="class-multiselect-wrap">
                                            <button
                                              type="button"
                                              className={`class-multiselect-trigger${
                                                openClassDropdownKey === key
                                                  ? " open"
                                                  : ""
                                              }`}
                                              onClick={() =>
                                                setOpenClassDropdownKey(prev =>
                                                  prev === key ? null : key
                                                )
                                              }
                                            >
                                              <span
                                                className={`class-multiselect-trigger-text${
                                                  mapping.classIds.length === 0
                                                    ? " placeholder"
                                                    : ""
                                                }`}
                                              >
                                                {mapping.classIds.length === 0
                                                  ? "Select Class(es)"
                                                  : classes
                                                      .filter(c =>
                                                        mapping.classIds.includes(
                                                          c.id
                                                        )
                                                      )
                                                      .map(c => c.class_name)
                                                      .join(", ")}
                                              </span>
                                              <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                className="class-multiselect-chevron"
                                              >
                                                <path
                                                  d="M2.5 4.5L6 8L9.5 4.5"
                                                  stroke="#6b7280"
                                                  strokeWidth="1.6"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                            </button>
                                            {openClassDropdownKey === key && (
                                              <div className="class-multiselect-panel">
                                                <div
                                                  className="class-multiselect-option class-multiselect-option-all"
                                                  onClick={() => {
                                                    const allSel =
                                                      classes.length > 0 &&
                                                      classes.every(c =>
                                                        mapping.classIds.includes(
                                                          c.id
                                                        )
                                                      )
                                                    setMappingState(prev => {
                                                      const cur = prev[key] || {
                                                        categoryId: "",
                                                        classIds: [],
                                                        subjects: [],
                                                      }
                                                      return {
                                                        ...prev,
                                                        [key]: {
                                                          ...cur,
                                                          classIds: allSel
                                                            ? []
                                                            : classes.map(
                                                                c => c.id
                                                              ),
                                                        },
                                                      }
                                                    })
                                                  }}
                                                >
                                                  <span
                                                    className={`subj-checkbox ${
                                                      classes.length > 0 &&
                                                      classes.every(c =>
                                                        mapping.classIds.includes(
                                                          c.id
                                                        )
                                                      )
                                                        ? "checked"
                                                        : ""
                                                    }`}
                                                  >
                                                    {classes.length > 0 &&
                                                      classes.every(c =>
                                                        mapping.classIds.includes(
                                                          c.id
                                                        )
                                                      ) && (
                                                        <svg
                                                          width="9"
                                                          height="9"
                                                          viewBox="0 0 10 10"
                                                          fill="none"
                                                        >
                                                          <path
                                                            d="M1.5 5L4 7.5L8.5 2.5"
                                                            stroke="#fff"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                        </svg>
                                                      )}
                                                  </span>
                                                  <span className="class-multiselect-option-label">
                                                    Select All
                                                  </span>
                                                </div>
                                                <div className="class-multiselect-divider" />
                                                {classes.map(cls => {
                                                  const checked =
                                                    mapping.classIds.includes(
                                                      cls.id
                                                    )
                                                  return (
                                                    <div
                                                      key={cls.id}
                                                      className="class-multiselect-option"
                                                      onClick={() =>
                                                        handleClassToggle(
                                                          key,
                                                          cls.id
                                                        )
                                                      }
                                                    >
                                                      <span
                                                        className={`subj-checkbox ${
                                                          checked
                                                            ? "checked"
                                                            : ""
                                                        }`}
                                                      >
                                                        {checked && (
                                                          <svg
                                                            width="9"
                                                            height="9"
                                                            viewBox="0 0 10 10"
                                                            fill="none"
                                                          >
                                                            <path
                                                              d="M1.5 5L4 7.5L8.5 2.5"
                                                              stroke="#fff"
                                                              strokeWidth="1.8"
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                            />
                                                          </svg>
                                                        )}
                                                      </span>
                                                      <span className="class-multiselect-option-label">
                                                        {cls.class_name}
                                                      </span>
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Subject table */}
                                    {mapping.categoryId &&
                                      mapping.classIds.length > 0 &&
                                      (mapping.subjects.length === 0 ? (
                                        <div
                                          className="text-center py-4"
                                          style={{
                                            color: "#9ca3af",
                                            fontSize: "13px",
                                          }}
                                        >
                                          No subjects found for this category.
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            overflowX: "auto",
                                            border: "1px solid #f3f4f6",
                                            borderRadius: "8px",
                                          }}
                                        >
                                          <Table className="mb-0 subject-mapping-table">
                                            <thead>
                                              <tr>
                                                <th
                                                  style={{
                                                    width: "52px",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <div
                                                    className="subj-checkbox-wrap"
                                                    onClick={() =>
                                                      handleSelectAllSubjects(
                                                        key
                                                      )
                                                    }
                                                    title={
                                                      allChecked
                                                        ? "Deselect all"
                                                        : "Select all"
                                                    }
                                                  >
                                                    <div
                                                      className={`subj-checkbox ${
                                                        allChecked
                                                          ? "checked"
                                                          : someChecked
                                                          ? "indeterminate"
                                                          : ""
                                                      }`}
                                                    >
                                                      {allChecked && (
                                                        <svg
                                                          width="9"
                                                          height="9"
                                                          viewBox="0 0 10 10"
                                                          fill="none"
                                                        >
                                                          <path
                                                            d="M1.5 5L4 7.5L8.5 2.5"
                                                            stroke="#fff"
                                                            strokeWidth="1.8"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                        </svg>
                                                      )}
                                                      {someChecked &&
                                                        !allChecked && (
                                                          <svg
                                                            width="9"
                                                            height="9"
                                                            viewBox="0 0 10 10"
                                                            fill="none"
                                                          >
                                                            <path
                                                              d="M2 5h6"
                                                              stroke="#fff"
                                                              strokeWidth="2"
                                                              strokeLinecap="round"
                                                            />
                                                          </svg>
                                                        )}
                                                    </div>
                                                  </div>
                                                </th>
                                                <th>
                                                  {isTopicWise
                                                    ? "Subject / Topic"
                                                    : "Subject"}
                                                </th>
                                                {!isGradeBased && (
                                                  <th
                                                    style={{
                                                      width: "130px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Min Marks
                                                  </th>
                                                )}
                                                {!isGradeBased && (
                                                  <th
                                                    style={{
                                                      width: "130px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Max Marks
                                                  </th>
                                                )}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {mapping.subjects.map((s, si) => {
                                                if (
                                                  s.rowType === "subject-header"
                                                ) {
                                                  return (
                                                    <tr
                                                      key={`header-${s.subject_code}-${si}`}
                                                      className="subject-header-row"
                                                    >
                                                      <td></td>
                                                      <td
                                                        colSpan={
                                                          isGradeBased ? 1 : 3
                                                        }
                                                        style={{
                                                          fontSize: "13px",
                                                          fontWeight: 700,
                                                          color: "#374151",
                                                        }}
                                                      >
                                                        {s.subject_name}
                                                      </td>
                                                    </tr>
                                                  )
                                                }
                                                return (
                                                  <tr
                                                    key={`row-${
                                                      s.subject_code
                                                    }-${
                                                      s.topic_id ?? ""
                                                    }-${si}`}
                                                    className={
                                                      s.checked
                                                        ? ""
                                                        : "row-unchecked"
                                                    }
                                                  >
                                                    <td
                                                      style={{
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      <div
                                                        className="subj-checkbox-wrap"
                                                        onClick={() =>
                                                          handleSubjectToggle(
                                                            key,
                                                            si
                                                          )
                                                        }
                                                      >
                                                        <div
                                                          className={`subj-checkbox ${
                                                            s.checked
                                                              ? "checked"
                                                              : ""
                                                          }`}
                                                        >
                                                          {s.checked && (
                                                            <svg
                                                              width="9"
                                                              height="9"
                                                              viewBox="0 0 10 10"
                                                              fill="none"
                                                            >
                                                              <path
                                                                d="M1.5 5L4 7.5L8.5 2.5"
                                                                stroke="#fff"
                                                                strokeWidth="1.8"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                              />
                                                            </svg>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </td>
                                                    <td
                                                      style={{
                                                        fontSize: "13px",
                                                        fontWeight: s.checked
                                                          ? 500
                                                          : 400,
                                                        color: s.checked
                                                          ? "#1a1d2e"
                                                          : "#9ca3af",
                                                      }}
                                                    >
                                                      {s.rowType === "topic" ? (
                                                        <span className="topic-row-label">
                                                          <span
                                                            style={{
                                                              color: "#9ca3af",
                                                              marginRight:
                                                                "6px",
                                                            }}
                                                          >
                                                            ↳
                                                          </span>
                                                          {s.topic_name}
                                                        </span>
                                                      ) : (
                                                        s.subject_name
                                                      )}
                                                    </td>
                                                    {!isGradeBased && (
                                                      <td
                                                        style={{
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <input
                                                          type="number"
                                                          className="mark-input"
                                                          value={
                                                            s.min_mark ?? ""
                                                          }
                                                          onChange={e =>
                                                            handleMarkChange(
                                                              key,
                                                              si,
                                                              "min_mark",
                                                              e.target.value
                                                            )
                                                          }
                                                          min={0}
                                                          placeholder="0"
                                                          disabled={!s.checked}
                                                        />
                                                      </td>
                                                    )}
                                                    {!isGradeBased && (
                                                      <td
                                                        style={{
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <input
                                                          type="number"
                                                          className="mark-input"
                                                          value={
                                                            s.max_mark ?? ""
                                                          }
                                                          onChange={e =>
                                                            handleMarkChange(
                                                              key,
                                                              si,
                                                              "max_mark",
                                                              e.target.value
                                                            )
                                                          }
                                                          min={0}
                                                          placeholder="0"
                                                          disabled={!s.checked}
                                                        />
                                                      </td>
                                                    )}
                                                  </tr>
                                                )
                                              })}
                                            </tbody>
                                          </Table>
                                        </div>
                                      ))}
                                  </>
                                )}
                              </div>
                            </div>
                          )
                        })
                      )}

                      <div
                        className="d-flex justify-content-between mt-4"
                        style={{
                          borderTop: "1px solid #f3f4f6",
                          paddingTop: "16px",
                        }}
                      >
                        <Button color="secondary" onClick={handlePrevious}>
                          Back
                        </Button>
                        <Button
                          color="success"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Exam"}
                        </Button>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ExamUpdateForm
